import m from 'mithril'
import dayjs from 'dayjs'
import dayjs_utc from 'dayjs/plugin/utc'
import dayjs_tz from 'dayjs/plugin/timezone'
dayjs.extend(dayjs_utc)
dayjs.extend(dayjs_tz)
import cloneDeep from 'lodash/cloneDeep'

import NotifyModel from '@app/models/notify'
import { OFFSET_REFERENCE_POINT_ENUM } from '@app/models/optimus-configuration'
import { MAXIMUM_PERIOD_DAYS } from '@app/helpers/att-args'

import client from '@shared/utils/client-rest'
import { OPTIMIZATION_JOB_STATUS, OPTIMIZATION_TYPE } from '@shared/constants'

import { minimizeID } from '@shared/utils/uuid-compression'
import { OPTIMUS_JOB_SIGNAL_STATUS } from '@app/components/optimus-configuration/helpers/optimusHelpers'
import UserModel from '@app/models/users'
import type {
	IOptimusJobInputs,
	IOptimusJobInputSignal,
} from '@app/components/report-optimus/components/optimus-jobs.interface'
import {
	fetchIsOptimusEnabledFn,
	fetchOptimusJobDetailsFn,
	fetchOptimusV2CustomerJobs,
	fetchSplitAnalysisDataFn,
	hasUnresolvedDeployedParams,
	type DeployedResolutionResponse,
	type IOptimusDetailedJob,
	type IOptimusJob,
	type SubmitOptimusJobBody,
	submitOptimusJob,
	updateJobFlagsFn,
	updateJobNameFn,
} from '@app/queries/optimus/optimus-report-request.ts'
import {
	constructSubmitJobBody,
	convertDetailedJobToSubmitBody,
	getJobSettingsDeployedById,
	getJobSettingsSignals,
	normalizeJobSettingsConfig,
	normalizeSplitConstraints,
} from '#frontend/models/optimus/optimus-jobs/helpers/submit-job.ts'
import { submitOptimusJobBodySchema } from '@shared-schemas/optimus/optimus-jobs'
import * as v from 'valibot'
import type { IRegionSpecsSignal } from '#frontend/queries/specs.ts'

export const LIMIT_ACTIVE_JOBS = 5

export const DEFAULT_SETTINGS_BY_OPTIMIZATION_TYPE = {
	offset: {
		weights: {
			offset: {
				aoy_weight: 1.0,
			},
		},
	},
}

export const generateOptimizationType = (
	current_opt_type: string | null,
	opt_type: string,
	is_removing_opt_type: boolean,
): string | null | undefined => {
	if (opt_type === OPTIMIZATION_TYPE.offset) {
		// Adding offset
		if (!is_removing_opt_type) {
			if (current_opt_type === OPTIMIZATION_TYPE.offset_split) {
				return OPTIMIZATION_TYPE.offset_split
			} else if (current_opt_type === OPTIMIZATION_TYPE.split) {
				return OPTIMIZATION_TYPE.offset_split
			} else if (current_opt_type === null) {
				return OPTIMIZATION_TYPE.offset
			} else {
				console.log('Unknown optimization type. Returning current opt type.')
				return current_opt_type
			}
		}
		// Removing offset
		else {
			if (current_opt_type === OPTIMIZATION_TYPE.offset_split) {
				return OPTIMIZATION_TYPE.split
			} else if (current_opt_type === OPTIMIZATION_TYPE.offset) {
				return null
			} else {
				console.log('Unknown optimization type. Returning current opt type.')
				return current_opt_type
			}
		}
	}

	if (opt_type === OPTIMIZATION_TYPE.split) {
		// Adding split
		if (!is_removing_opt_type) {
			if (current_opt_type === OPTIMIZATION_TYPE.offset_split) {
				return OPTIMIZATION_TYPE.offset_split
			} else if (current_opt_type === OPTIMIZATION_TYPE.offset) {
				return OPTIMIZATION_TYPE.offset_split
			} else if (current_opt_type === null) {
				return OPTIMIZATION_TYPE.split
			} else {
				console.log('Unknown optimization type. Returning current opt type.')
				return current_opt_type
			}
		}
		// Removing split
		else {
			if (current_opt_type === OPTIMIZATION_TYPE.offset_split) {
				return OPTIMIZATION_TYPE.offset
			} else if (current_opt_type === OPTIMIZATION_TYPE.split) {
				return null
			} else {
				console.log('Unknown optimization type. Returning current opt type.')
				return current_opt_type
			}
		}
	}
	return undefined
}

export const DEFAULT_SIGNAL_SETTINGS = {
	cycle_length_constraints: {
		enable_optimization: true,
		current_cycle_length: 100,
		min_cycle_length: null,
		max_cycle_length: null,
	},
	offset_constraints: {
		enable_optimization: true,
		current_offset: 100,
		offset_reference_point: OFFSET_REFERENCE_POINT_ENUM.lead,
	},
}

// Minimum cycle length the entered min splits demand: the larger of the two rings'
// summed min splits, with the ring membership taken from the signal's sequence.
// On initial load only offset_constraints.sequence is populated, so fall back to it.
const getRequiredCycleFromMinSplits = (signal: IOptimusJobInputSignal): number => {
	const sequence = signal.sequence_constraints?.sequence ?? signal.offset_constraints?.sequence
	if (!sequence?.rings?.length) return 0

	const sumRing = (ring: NonNullable<typeof sequence>['rings'][number]): number =>
		ring.reduce((acc, slot) => {
			if (slot.type !== 'phase' || slot.phase === 0) return acc
			const sc = signal.split_constraints?.find((c) => Number(c.number) === slot.phase)
			return acc + (sc?.min_split ?? 0)
		}, 0)

	return Math.max(...sequence.rings.map((ring) => sumRing(ring)))
}

export const generateJobSettingsErrors = (
	proposed_job_settings: IOptimusJobInputs | null,
	jobs: (IOptimusJob | IOptimusDetailedJob)[],
	signal_specs: IRegionSpecsSignal[],
	deployed_resolution?: DeployedResolutionResponse | null,
) => {
	const errors: Record<string, string> = {}

	if (!proposed_job_settings) {
		errors.settings = 'Form: Job settings must exist.'
		return
	}

	const {
		customer_id,
		network_id,
		tod,
		reference_time_period,
		timing_parameters,
		cycle_length_constraints,
		objectives,
		signals_optimization: signals,
	} = proposed_job_settings

	// const _signals_in_optimization_data = signals.filter((sig) => signals_in_optimization.includes(sig.id))

	if (!customer_id) errors.customer_id = 'Customer required.'
	if (!network_id) errors.network_id = 'Network required.'

	if (!timing_parameters || Object.values(timing_parameters).every((v) => !v))
		errors.timing_parameters = 'At least 1 Timing Parameter required.'

	if (!signal_specs.every((s) => s.optimus_job_signal_status)) {
		errors['signals.must_check_for_min_config'] = 'All Signals must be checked for minimum Optimus Configuration.'
	}
	if (
		!signals ||
		!signals.length ||
		(signals &&
			!signals?.some((s) => {
				const s_specs = signal_specs.find((specs) => specs.id === s.id)
				return (
					s_specs?.optimus_job_signal_status != null &&
					[OPTIMUS_JOB_SIGNAL_STATUS.MET_MINIMUM, OPTIMUS_JOB_SIGNAL_STATUS.COMPLETE].includes(
						s_specs.optimus_job_signal_status,
					) &&
					s.enable_optimization
				)
			}))
	) {
		errors['signals.at_least_one_min_config_signal'] = 'At least 1 Signal with minimum Optimus Configuration required.'
	}
	if (
		signals.length &&
		signals?.some((s) => {
			const s_specs = signal_specs.find((specs) => specs.id === s.id)
			return (
				s_specs?.optimus_job_signal_status == null ||
				![OPTIMUS_JOB_SIGNAL_STATUS.MET_MINIMUM, OPTIMUS_JOB_SIGNAL_STATUS.COMPLETE].includes(
					s_specs.optimus_job_signal_status,
				)
			)
		})
	) {
		errors['signals.below_min_config_cannot_participate'] =
			'Signals without minimum Optimus Configuration cannot participate in optimization.'
	}

	if (signals.length && timing_parameters?.offsets && !signals.some((s) => s.offset_constraints))
		errors['signals.offset_constraints'] = 'At least 1 Signal required for Offset Optimization.'
	if (signals.length && timing_parameters?.cycle_lengths && !signals.some((s) => s.cycle_length_constraints))
		errors['signals.cycle_length_constraints'] = 'At least 1 Signal required for Cycle Length Optimization.'
	// const signals_in_cycle_length_optimization = signals.filter(s => s.cycle_length_constraints?.enable_optimization)
	// if (signals.length && timing_parameters?.cycle_lengths && !signals_in_cycle_length_optimization.every(s => s.cycle_length_constraints?.current_cycle_length === signals_in_cycle_length_optimization?.[0]?.cycle_length_constraints?.current_cycle_length)) errors['signals.cycle_length_constraints.different_cycle_length'] = 'Signal with different cycle length detected. De-select to re-enable Cycle Length Optimization.'
	if (signals.length && timing_parameters?.splits && !signals.some((s) => s.cycle_length_constraints))
		errors['signals.split_constraints'] = 'At least 1 Signal required for Split Optimization.'

	if (
		signals.length &&
		signals.some((s) => s.offset_constraints?.current_offset != null && s.offset_constraints.current_offset < 0)
	)
		errors['signals.offsets_cannot_be_negative'] = 'Offsets cannot be negative.'
	if (
		signals.length &&
		signals.some(
			(s) =>
				(s.cycle_length_constraints?.current_cycle_length != null &&
					s.cycle_length_constraints.current_cycle_length < 0) ||
				(s.cycle_length_constraints?.min_cycle_length != null && s.cycle_length_constraints.min_cycle_length < 0) ||
				(s.cycle_length_constraints?.max_cycle_length != null && s.cycle_length_constraints.max_cycle_length < 0),
		)
	)
		errors['signals.cycle_lengths_cannot_be_negative'] = 'Cycle lengths cannot be negative.'
	if (
		signals.length &&
		signals.some((s) =>
			s.split_constraints?.some(
				(sc) =>
					(sc?.current_split != null && sc.current_split < 0) ||
					(sc?.min_split != null && sc.min_split < 0) ||
					(sc?.max_split != null && sc.max_split < 0),
			),
		)
	)
		errors['signals.splits_cannot_be_negative'] = 'Splits cannot be negative.'
	if (
		signals.length &&
		signals.some(
			(s) =>
				!(s.split_constraints?.some((sc) => sc?.is_enabled) && s.split_constraints?.some((sc) => sc?.is_coordinated)),
		)
	)
		errors['signals.all_signals_must_have_enabled_and_coordinated_phases'] =
			'All signals must have at least 1 enabled and 1 coordinated phase.'
	if (
		signals.length &&
		signals.some(
			(s) =>
				s.enable_optimization &&
				!s.offset_constraints.enable_optimization &&
				!s.cycle_length_constraints.enable_optimization &&
				!s.enable_split_optimization,
		)
	)
		errors['signals.optimized_signals_must_optimize_something'] =
			'All signals included in optimization must enable optimization for at least Offsets, Cycles, or Splits.'
	if (
		signals.length &&
		signals.some(
			(s) =>
				(!timing_parameters?.cycle_lengths ||
					!s.enable_optimization ||
					!s.cycle_length_constraints.enable_optimization) &&
				s.cycle_length_constraints.current_cycle_length == null,
		)
	)
		errors['signals.nonoptimized_signals_must_have_cycle_length'] =
			'Signals not optimizing Cycles must have a defined Cycle Length.'
	if (
		signals.length &&
		signals.some(
			(s) =>
				(!timing_parameters?.offsets || !s.enable_optimization || !s.offset_constraints.enable_optimization) &&
				s.offset_constraints.current_offset == null,
		)
	)
		errors['signals.nonoptimized_signals_must_have_offset'] =
			'Signals not optimizing Offsets must have a defined Offset.'
	if (
		signals.length &&
		signals.some(
			(s) =>
				(!timing_parameters?.offsets || !s.enable_optimization || !s.offset_constraints.enable_optimization) &&
				s.offset_constraints.offset_reference_point == null,
		)
	)
		errors['signals.nonoptimized_signals_must_have_offset_reference_point'] =
			'Signals not optimizing Offsets must have a defined Offset Reference Point.'
	// Splits for a non-optimized signal are taken from the resolved deployed plan, not from what the
	// modal shows, so the check is against the resolution rather than the displayed value.
	const unresolved_nonoptimized = signals.filter(
		(s) =>
			(!timing_parameters?.splits || !s.enable_optimization || !s.enable_split_optimization) &&
			hasUnresolvedDeployedParams(deployed_resolution, s.id),
	)
	if (unresolved_nonoptimized.length) {
		const names = unresolved_nonoptimized
			.map((s) => signal_specs.find((spec) => spec.id === s.id)?.name_short ?? s.id)
			.sort()
		const is_one = names.length === 1
		errors['signals.nonoptimized_signals_must_have_splits'] =
			`${names.join(', ')} ran more than one set of timing parameters over this time period. ` +
			`Either include ${is_one ? 'it' : 'them'} in optimization, or pick a time period where ${is_one ? 'its' : 'their'} timing stays constant.`
	}

	// Each ring's summed min splits must fit within the cycle the signal will run: the
	// selected cycle length(s) if any, otherwise the signal's own current cycle length.
	const numeric_selected_cycles = (cycle_length_constraints?.selected_cycle_lengths ?? [])
		.map((cycle) => Number(cycle))
		.filter((cycle) => Number.isFinite(cycle))
	if (signals.length) {
		const min_splits_exceed_cycle = signals.some((s) => {
			if (!s.enable_optimization) return false
			const required_cycle = getRequiredCycleFromMinSplits(s)
			if (!required_cycle) return false
			const current_cycle = s.cycle_length_constraints?.current_cycle_length
			const cycles_to_check = numeric_selected_cycles.length
				? numeric_selected_cycles
				: current_cycle != null
					? [current_cycle]
					: []
			return cycles_to_check.some((cycle) => required_cycle > cycle)
		})
		if (min_splits_exceed_cycle)
			errors['signals.min_splits_exceed_selected_cycle'] =
				'Minimum splits for the current sequence exceed a cycle length. Lower the minimum splits or raise the cycle length.'
	}

	if (!tod) errors['tod'] = 'Reference Time Period: TOD required.'
	const tod_days = tod?.time_of_day_data?.days
	const tod_local_start_time = tod?.time_of_day_data?.local_start_time ?? tod?.local_start_time
	const tod_local_end_time = tod?.time_of_day_data?.local_end_time ?? tod?.local_end_time
	if (tod && !Object.values(tod_days ?? {}).filter((x) => x).length)
		errors['tod.days_missing'] = 'Reference Time Period: Days of week required.'
	if (tod && !tod_local_start_time)
		errors['tod.local_start_time_missing'] = 'Reference Time Period: TOD start time required.'
	if (tod && !tod_local_end_time) errors['tod.local_end_time_missing'] = 'Reference Time Period: TOD end time required.'
	if (tod && tod_local_start_time && tod_local_end_time) {
		const [start_hour, start_minute, _] = tod_local_start_time.split(':')
		const [end_hour, end_minute, __] = tod_local_end_time.split(':')
		const _start_time = dayjs().hour(parseInt(start_hour)).minute(parseInt(start_minute))
		const _end_time = dayjs().hour(parseInt(end_hour)).minute(parseInt(end_minute))
		if (_start_time.isAfter(_end_time)) {
			errors['tod.start_time_must_be_before_end_time'] =
				'Reference Time Period: TOD start time must be before end time.'
		}
	}

	if (!reference_time_period) errors.reference_time_period = 'Reference Time Period required.'
	// To slim down error messaging, check date range only once a TOD is set
	if (tod) {
		if (!reference_time_period?.date_start && !reference_time_period.date_end) {
			errors.reference_time_period = 'Reference Time Period: Date Range required.'
		} else if (!reference_time_period?.date_start) {
			errors.reference_time_period = 'Reference Time Period: Date Start required.'
		} else if (!reference_time_period?.date_end) {
			errors.reference_time_period = 'Reference Time Period: Date End required.'
		} else if (reference_time_period?.date_start && reference_time_period?.date_end) {
			const start_date = dayjs.utc(reference_time_period.date_start)
			const end_date = dayjs.utc(reference_time_period.date_end).subtract(1, 'day') // subtract, since data is exclusive of end date, but UX is based on inclusive end date
			const period_days = end_date.diff(dayjs.utc(start_date), 'days')
			if (period_days > MAXIMUM_PERIOD_DAYS)
				errors['reference_time_period.maximum_period_days'] =
					`Reference Time Period: Time interval cannot exceed ${MAXIMUM_PERIOD_DAYS} days. (Currently: ${period_days})`
			if (start_date.isAfter(end_date))
				errors['reference_time_period.invalid_start_end_dates'] =
					`Reference Time Period: Start date cannot be after end date.`
		}
	}

	if (!cycle_length_constraints) errors.cycle_length_constraints = 'Cycle Length General Settings required.'

	if (!objectives) errors.objectives = 'Objectives required.'

	let num_running_jobs = 0
	let num_queued_jobs = 0
	for (const job of jobs || []) {
		if (job.status === OPTIMIZATION_JOB_STATUS.running) {
			num_running_jobs += 1
		} else if (job.status === OPTIMIZATION_JOB_STATUS.queued) {
			num_queued_jobs += 1
		}
	}
	const num_active_jobs = num_running_jobs + num_queued_jobs
	// if (num_active_jobs >= LIMIT_ACTIVE_JOBS)
	// 	errors.submit =
	// 		`Cannot have more than ${LIMIT_ACTIVE_JOBS} active jobs (Currently, ${num_running_jobs} running and ${num_queued_jobs} queued).`
	const is_super_admin = UserModel.me?.email?.endsWith('@flowlabs.ai')
	if (!is_super_admin && num_active_jobs >= LIMIT_ACTIVE_JOBS)
		errors.submit = `Cannot have more than ${LIMIT_ACTIVE_JOBS} active jobs (Currently, ${num_running_jobs} running and ${num_queued_jobs} queued).`

	return errors
}

class Cache {
	jobs: IOptimusJob[]
	network_id: string | undefined
	is_optimus_enabled: boolean | null
	jobs_loading: boolean
	job_details_loading: boolean
	prefs: Record<string, any> | undefined
	prefs_loading: boolean
	submit_prefs_loading: boolean
	split_analysis_data: any
	split_analysis_loading: boolean
	connection_volumes_by_signal: Record<string, any[]>
	connection_volumes_loading: boolean
	coordination_comparison_data: any
	coordination_comparison_loading: boolean
	default_new_job_settings: Partial<IOptimusJobInputs>
	current_new_job_settings: IOptimusJobInputs | null
	pending_clone_settings:
		| (SubmitOptimusJobBody & {
				settings_signals: ReturnType<typeof getJobSettingsSignals>
				deployed_signals: NonNullable<IOptimusDetailedJob['settings']['signals_deployed']>
				split_input: IOptimusDetailedJob['split_input']
				offset_input: IOptimusDetailedJob['offset_input']
		  })
		| null
	pending_clone_parent_id: string | null
	submit_loading: boolean
	errors: Record<string, string>

	constructor() {
		this.network_id
		this.is_optimus_enabled = null

		this.jobs_loading = false
		this.job_details_loading = false
		this.prefs_loading = false
		this.submit_prefs_loading = false
		this.submit_loading = false

		this.jobs = []
		this.prefs
		this.split_analysis_data = null
		this.split_analysis_loading = false
		this.connection_volumes_by_signal = {}
		this.connection_volumes_loading = false
		this.coordination_comparison_data = null
		this.coordination_comparison_loading = false

		// Form state
		this.default_new_job_settings = {
			timing_parameters: {
				offsets: true,
				cycle_lengths: true,
				splits: true,
				phase_sequence: false,
			},
			cycle_length_constraints: {
				selected_cycle_lengths: [],
				match_cycle_lengths: true,
				allow_half_cycles: false,
			},
			objectives: {
				directional_bias: {
					weight: 0, // [-1, 0, 1], direction 1, balanced, direction 2
				},

				target_speed_mph: null, // NULL = "prevailing speed"

				safety: 0, // [-2, -1, 0, 1, 2], Maximize AOG <-> Minimize DZE

				bandwidth: 0, // [-2, -1, 0, 1, 2], Maximize AOG <-> Maximize Bandwidth

				side_street_control_delay_tolerance: 0,
			},
		}
		// NB: Unlike prefs, current_new_job_settings contains runtime-specified job params
		this.current_new_job_settings = null
		this.pending_clone_settings = null
		this.pending_clone_parent_id = null
		this.errors = {}
	}

	/**
	 * Fetch signal specs and TMS phase mappings for each signal in the network scanner,
	 * then derive phase label positions from approach edge geometry.
	 * Only phases present in the TMS Phase Mappings are displayed.
	 */

	async fetchIsOptimusEnabled(network_id: string) {
		try {
			// Add a timeout to prevent hanging
			// const timeoutPromise = new Promise(
			// 	(_, reject) => setTimeout(() => reject(new Error('Request timeout')), 10000), // 10 second timeout
			// )

			const response = await fetchIsOptimusEnabledFn(network_id)

			this.is_optimus_enabled = response?.enabled ?? false
		} catch (error) {
			console.error('Error fetching optimus status:', error)
			this.is_optimus_enabled = false
		}

		return m.redraw()
	}

	async fetchOptimusJobsForCustomer(customer_id: string) {
		this.jobs_loading = true

		return fetchOptimusV2CustomerJobs(customer_id).then(
			(new_jobs_v2) => {
				const new_jobs = ([] as IOptimusJob[]).concat(new_jobs_v2?.length ? new_jobs_v2 : [])

				if (Array.isArray(new_jobs)) {
					const existing_jobs_map = new Map((this.jobs || []).map((j) => [j.job_id, j]))
					this.jobs = new_jobs.map((job) => {
						const existing = existing_jobs_map.get(job.job_id)
						// Fresh summary fields (status, dates, name) must win so a refresh reflects server changes;
						// `existing` is spread first only to retain detail-only fields (settings, split/offset input)
						// loaded separately via fetchOptimusJobDetails, which the summary list response omits.
						return existing ? { ...existing, ...job } : job
					})
				} else {
					this.jobs = []
				}
				this.jobs_loading = false

				m.redraw()
			},
		)
	}

	async updateJobName(job_id: string, job_name: string) {
		const result = await updateJobNameFn(job_id, job_name)
		const job = this.jobs?.find((j) => j.job_id === job_id)
		if (job) job.job_name = job_name
		m.redraw()
		return result
	}

	async updateJobFlags(job_id: string, flags: { is_visible?: boolean; is_remote?: boolean }) {
		const result = await updateJobFlagsFn(job_id, flags)
		const job = this.jobs?.find((j) => j.job_id === job_id)
		if (job && result) {
			job.is_visible = result.is_visible
			job.is_remote = result.is_remote
		}
		m.redraw()
		return result
	}

	async fetchOptimusJobDetails(customer_id: string, job_id: string, version: number) {
		this.job_details_loading = true

		const api_endpoint =
			version === 2 ? `/api/v1/pages/optimus-v2/job-details/${job_id}` : `/api/v1/pages/optimus/job-details/${job_id}`

		return fetchOptimusJobDetailsFn(job_id, version)
			.then(async (new_job_details) => {
				if (version != null) new_job_details.version = version

				if (version === 2) {
					// BEGIN TEMP - Minimize some IDs
					new_job_details.split_input =
						new_job_details.split_input &&
						Object.fromEntries(
							Object.entries(new_job_details.split_input).map(([sig_id, output]) => [minimizeID(sig_id), output]),
						)
					new_job_details.split_output =
						new_job_details.split_output &&
						Object.fromEntries(
							Object.entries(new_job_details.split_output).map(([sig_id, output]) => [minimizeID(sig_id), output]),
						)
					if (new_job_details.offset_input?.length === 2) {
						new_job_details.offset_input[0].signals = new_job_details.offset_input[0].signals?.map((sig) => ({
							...sig,
							signal_id: minimizeID(sig?.signal_id),
							upstream_signal_id:
								sig?.upstream_signal_id == null ? sig?.upstream_signal_id : minimizeID(sig?.upstream_signal_id),
							downstream_signal_id:
								sig?.downstream_signal_id == null ? sig?.downstream_signal_id : minimizeID(sig?.downstream_signal_id),
						}))
						new_job_details.offset_input[1].signals = new_job_details.offset_input[1].signals?.map((sig) => ({
							...sig,
							signal_id: minimizeID(sig?.signal_id),
							upstream_signal_id:
								sig?.upstream_signal_id == null ? sig?.upstream_signal_id : minimizeID(sig?.upstream_signal_id),
							downstream_signal_id:
								sig?.downstream_signal_id == null ? sig?.downstream_signal_id : minimizeID(sig?.downstream_signal_id),
						}))
					}
					if (new_job_details.offset_output?.length === 2) {
						new_job_details.offset_output[0].signals = new_job_details.offset_output[0].signals?.map((sig) => ({
							...sig,
							signal_id: minimizeID(sig?.signal_id),
							upstream_signal_id:
								sig?.upstream_signal_id == null ? sig?.upstream_signal_id : minimizeID(sig?.upstream_signal_id),
							downstream_signal_id:
								sig?.downstream_signal_id == null ? sig?.downstream_signal_id : minimizeID(sig?.downstream_signal_id),
						}))
						new_job_details.offset_output[1].signals = new_job_details.offset_output[1].signals?.map((sig) => ({
							...sig,
							signal_id: minimizeID(sig?.signal_id),
							upstream_signal_id:
								sig?.upstream_signal_id == null ? sig?.upstream_signal_id : minimizeID(sig?.upstream_signal_id),
							downstream_signal_id:
								sig?.downstream_signal_id == null ? sig?.downstream_signal_id : minimizeID(sig?.downstream_signal_id),
						}))
					}
					// END TEMP - Minimize some IDs
				}

				if (this.jobs) {
					const job_idx = this.jobs.findIndex((j) => j.job_id === job_id)
					this.jobs[job_idx] = { ...new_job_details, job_id }
				} else {
					this.jobs = [{ ...new_job_details, job_id }]
				}
			})
			.finally(() => {
				this.job_details_loading = false

				m.redraw()
			})
	}

	async fetchSplitAnalysisData(job_id: string) {
		this.split_analysis_loading = true
		this.split_analysis_data = null

		return fetchSplitAnalysisDataFn(job_id)
			.then((data) => {
				this.split_analysis_data = data?.edge_connection_map ?? {}
			})
			.finally(() => {
				this.split_analysis_loading = false
				m.redraw()
			})
	}

	async fetchCoordinationComparison(job_id: string) {
		this.coordination_comparison_loading = true
		this.coordination_comparison_data = null

		return client(`/api/v1/pages/optimus-v2/coordination-split-comparison/${job_id}`)
			.then((data) => {
				this.coordination_comparison_data = data.comparison || {}
			})
			.finally(() => {
				this.coordination_comparison_loading = false
				m.redraw()
			})
	}

	async fetchConnectionVolumes(network_id: string, signal_ids: string[], job_settings: any, customer_id: string) {
		this.connection_volumes_loading = true
		this.connection_volumes_by_signal = {}

		const tod = job_settings.tod
		const ref = job_settings.reference_time_period

		const start = new Date(ref.date_start)
		const end = new Date(ref.date_end)
		const period_days = Math.ceil(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1

		const params = {
			customer_id,
			tod_params: {
				has_monday: tod.days_boolean?.has_monday ?? tod.days?.includes?.('Monday') ?? false,
				has_tuesday: tod.days_boolean?.has_tuesday ?? tod.days?.includes?.('Tuesday') ?? false,
				has_wednesday: tod.days_boolean?.has_wednesday ?? tod.days?.includes?.('Wednesday') ?? false,
				has_thursday: tod.days_boolean?.has_thursday ?? tod.days?.includes?.('Thursday') ?? false,
				has_friday: tod.days_boolean?.has_friday ?? tod.days?.includes?.('Friday') ?? false,
				has_saturday: tod.days_boolean?.has_saturday ?? tod.days?.includes?.('Saturday') ?? false,
				has_sunday: tod.days_boolean?.has_sunday ?? tod.days?.includes?.('Sunday') ?? false,
				local_start_time: tod.local_start_time,
				local_end_time: tod.local_end_time,
				timezone_iana: ref.timezone_iana,
			},
			date_range: {
				start_date: ref.date_start,
				end_date: ref.date_end,
				exclude_holidays: ref.exclude_holidays ?? false,
				period_days,
				timezone_iana: ref.timezone_iana,
			},
		}

		try {
			const results = await Promise.all(
				signal_ids.map((signal_id) =>
					client(`/api/v1/pages/network/${network_id}/signal/${signal_id}/metrics`, { params })
						.then((data) => ({ signal_id, connection_volumes: data?.connection_volumes || [] }))
						.catch(() => ({ signal_id, connection_volumes: [] })),
				),
			)
			for (const { signal_id, connection_volumes } of results) {
				this.connection_volumes_by_signal[signal_id] = connection_volumes
			}
		} catch (err) {
			console.error('Error fetching connection volumes:', err)
		} finally {
			this.connection_volumes_loading = false
			m.redraw()
		}
	}

	// Not cached to ensure prefs are most up-to-date
	async fetchOptimusPreferences(network_id: string) {
		this.prefs_loading = true

		return client(`/api/v1/pages/optimus/get-preferences`).then(async (res) => {
			const new_prefs = res?.preferences ?? {}

			this.prefs = new_prefs
			this.default_new_job_settings = Object.assign(this.default_new_job_settings, this.prefs?.[network_id])
			if (this.current_new_job_settings == null) {
				this.current_new_job_settings = cloneDeep(this.default_new_job_settings)
			}
			this.prefs_loading = false

			m.redraw()
		})
	}

	async submitOptimusPreferences(network_id: string, prefs_for_network) {
		this.submit_prefs_loading = true

		// Do not store job params that are specified during runtime
		delete prefs_for_network.customer_id
		delete prefs_for_network.tod_id
		delete prefs_for_network.network_id

		const new_prefs = Object.assign({}, this.prefs, { [network_id]: prefs_for_network })

		return client(`/api/v1/pages/optimus/update-preferences`, {
			method: 'PUT',
			body: {
				preferences: new_prefs,
			},
		}).then(async (res) => {
			const updated_prefs = res.preferences

			this.prefs = updated_prefs
			this.default_new_job_settings = Object.assign(this.default_new_job_settings, cloneDeep(updated_prefs[network_id]))
			this.current_new_job_settings = Object.assign(
				this.current_new_job_settings || {},
				cloneDeep(updated_prefs[network_id]),
			)
			this.submit_prefs_loading = false

			m.redraw()
		})
	}

	async submitOptimusJob(signal_specs: IRegionSpecsSignal[]) {
		this.validateJobSettings(signal_specs)

		const has_current_errors = Object.keys(this.errors).length > 0
		if (has_current_errors) {
			const err_list_msg = Object.values(this.errors)
				.map((v) => `\n${v}`)
				.join('')
			throw new Error(`Cannot submit job. Parameter errors exist: ${err_list_msg}`)
		}

		if (!this.current_new_job_settings) {
			return
		}

		this.submit_loading = true

		const { customer_id } = this.current_new_job_settings

		const new_job_body = constructSubmitJobBody(signal_specs, this.current_new_job_settings)

		const clone_parent_id = this.pending_clone_parent_id
		this.pending_clone_parent_id = null

		const full_job_body = {
			...new_job_body,
			...(clone_parent_id ? { parent_id: clone_parent_id } : {}),
			dry_run: false,
		}

		// The toggles are super-admin-only UI; anything submitted by a customer is always visible and remote.
		if (!UserModel.is_super_admin) {
			full_job_body.is_visible = true
			full_job_body.is_remote = true
		}

		const _diag = v.safeParse(submitOptimusJobBodySchema({ expected_format: 'short', convert_format: false }), full_job_body)
		if (!_diag.success) {
			console.log(
				'submitOptimusJob validation issues:',
				_diag.issues.map((issue) => ({
					path: v.getDotPath(issue),
					expected: issue.expected,
					received: issue.received,
					message: issue.message,
				})),
			)
		}
		v.parse(submitOptimusJobBodySchema({ expected_format: 'short', convert_format: false }), full_job_body)

		return submitOptimusJob(full_job_body as unknown as SubmitOptimusJobBody)
			.then(async () => {
				this.current_new_job_settings = null
				// TODO: Extract this fetch out to separate submit vs get error handling
				return this.fetchOptimusJobsForCustomer(customer_id).catch((err) => {
					console.error(err?.message === '[object Object]' ? JSON.stringify(err) : err)
					NotifyModel.showNotify(`Error while fetching Jobs: ${err}.`)
				})
			})
			.finally(() => {
				this.submit_loading = false
				m.redraw()
			})
	}

	async resubmitJobWithConstraints(
		job: IOptimusDetailedJob,
		phase_constraints: Record<string, Record<number, { min?: number; max?: number }>>,
		cycle_overrides: Record<string, number> = {},
	) {
		const settings = normalizeJobSettingsConfig(cloneDeep(job.settings))

		for (const signal of getJobSettingsSignals(settings)) {
			const short_id = minimizeID(signal.id)

			const cycle_override = cycle_overrides[short_id]
			if (cycle_override != null && signal.cycle_length_constraints) {
				signal.cycle_length_constraints.current_cycle_length = null
				signal.cycle_length_constraints.min_cycle_length = cycle_override
				signal.cycle_length_constraints.max_cycle_length = cycle_override
			}

			const constraints = phase_constraints[short_id]
			if (!constraints) continue

			if (!signal.split_constraints) continue
			const sc_array = normalizeSplitConstraints(signal.split_constraints)
			for (const sc of sc_array) {
				const c = constraints[sc.number]
				if (!c) continue
				if (c.min != null) sc.min_split = c.min
				if (c.max != null) sc.max_split = c.max
				sc.is_enabled = true
			}
		}

		const all_mins = Object.values(phase_constraints).flatMap((phases) =>
			Object.values(phases)
				.map((c) => c.min)
				.filter((v): v is number => v != null),
		)
		const all_max_values = Object.values(phase_constraints).flatMap((phases) =>
			Object.values(phases)
				.map((c) => c.max)
				.filter((v): v is number => v != null),
		)
		const constraint_parts = [
			all_mins.length ? `Min: ${Math.min(...all_mins)}` : null,
			all_max_values.length ? `Max: ${Math.max(...all_max_values)}` : null,
		].filter((p): p is string => p !== null)
		const constraint_suffix = constraint_parts.length ? ` - (${constraint_parts.join(' - ')})` : ''

		const job_details_to_submit_body = convertDetailedJobToSubmitBody({
			...job,
			settings,
			job_name: (job.job_name ?? '') + constraint_suffix,
		})

		const new_job = {
			...job_details_to_submit_body,
			dry_run: false,
		}

		// The toggles are super-admin-only UI; anything submitted by a customer is always visible and remote.
		if (!UserModel.is_super_admin) {
			new_job.is_visible = true
			new_job.is_remote = true
		}
		// v.parse(submitOptimusJobBodySchema({ expected_format: 'short', convert_format: false }), new_job)

		this.submit_loading = true

		return submitOptimusJob(new_job)
			.then(async () => {
				return this.fetchOptimusJobsForCustomer(settings.customer_id).catch((err) => {
					console.error(err)
					NotifyModel.showNotify(`Error while fetching Jobs: ${err}.`)
				})
			})
			.finally(() => {
				this.submit_loading = false
				m.redraw()
			})
	}

	async prepareCloneJobSettings(job_id: string, customer_id: string, version: number) {
		await this.fetchOptimusJobDetails(customer_id, job_id, version)
		const job = this.jobs?.find((j) => j.job_id === job_id) as IOptimusDetailedJob | undefined
		if (!job?.settings) return
		const settings = normalizeJobSettingsConfig(job.settings)
		const submit_body = convertDetailedJobToSubmitBody({ ...job, settings })
		this.pending_clone_settings = {
			...submit_body,
			settings_signals: getJobSettingsSignals(settings),
			deployed_signals: settings.signals_deployed ?? [],
			split_input: job.split_input,
			offset_input: job.offset_input,
		}
		this.pending_clone_parent_id = submit_body.parent_id ?? null
		m.redraw()
	}

	validateJobSettings(signal_specs: IRegionSpecsSignal[]) {
		this.errors = Object.assign(
			{},
			this.errors.submit ? { submit: this.errors.submit } : {}, // errors['submit] is managed externally by forms
			generateJobSettingsErrors(this.current_new_job_settings, this.jobs, signal_specs),
		)
	}
}

export default new Cache()
